import * as React from "react";
import {
  BaseEdge,
  useInternalNode,
  useStore,
  type EdgeProps,
} from "@xyflow/react";

import {
  WORKSPACE_EDGE_STUB_BASE,
  WORKSPACE_GRAPH_COLUMN_GAP,
  WORKSPACE_NODE_CARD_HEIGHT,
  WORKSPACE_NODE_CARD_WIDTH,
} from "../workspaceGraphConstants";
import { getWorkspaceFloatingEdgeParams } from "../workspaceFloatingEdgeUtils";

const EDGE_STROKE = "color-mix(in oklab, var(--primary) 58%, var(--foreground) 18%)";
const LOADING_PATH_STROKE = "var(--brand-neon-aqua)";
/** Keep in sync with `WORKSPACE_POLICY_COMBINED_SOURCE_STROKE` in workspaceNodeIcon. */
const COMBINED_SOURCE_STROKE = "rgb(245 158 11)";
const PLACEHOLDER_EDGE_STROKE =
  "color-mix(in oklab, var(--muted-foreground) 55%, var(--border))";
const PIPELINE_ROW_ALIGNMENT_PX = 8;

export type WorkspacePipelineEdgeData = {
  /** True when this edge lies on the path into a loading/processing node. */
  loadingPath?: boolean;
  /** Outgoing index among siblings from the same source (sorted top→bottom). */
  lane?: number;
  laneCount?: number;
  /** Simulation → simulation rerun stack (bottom → top handles). */
  verticalStack?: boolean;
  /**
   * Derived Edit & run: policy→policy / CP→CP stacks. (Fork sims park
   * downstream of the policy, so policy→sim uses normal L→R handles.)
   */
  centerAim?: boolean;
  /** Canvas focus filter — fade edges that don't touch a matched node. */
  focusFilterDimmed?: boolean;
  /** Edge from a source policy into the selected combined_policy. */
  combinedSourceHighlight?: boolean;
  /**
   * Executing-account link: Y for the long horizontal that runs underneath the
   * combined-policy corridor, then returns to the diamond. Nodes stay on the
   * parent spine — only the edge detours.
   */
  execDetourY?: number;
};

export type WorkspacePlaceholderEdgeData = {
  /**
   * Real next-step siblings sharing this parent's trunk. When present, the light
   * stroke starts at the lowest sibling (split) instead of recoloring the trunk.
   */
  siblingNodeIds?: string[];
  lane?: number;
  laneCount?: number;
};

type EdgeObstacleBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

const OBSTACLE_CLEARANCE_PX = 10;

/**
 * Shared trunk X for every sibling leaving the same source. Fan-outs must share
 * one bend so overlapping verticals read as a single road with junctions, not
 * a ribbon of parallel lanes.
 */
function pipelineBendX(sourceX: number, targetX: number): number {
  const deltaX = targetX - sourceX;
  const maxStub = Math.max(12, Math.abs(deltaX) - 24);
  const stub = Math.min(maxStub, WORKSPACE_EDGE_STUB_BASE);
  if (deltaX >= 0) {
    return sourceX + Math.max(12, stub);
  }
  // Right→left (e.g. policy→sim): bend in the gutter left of the source.
  return sourceX - Math.max(12, stub);
}

function horizontalHitsObstacles(
  x1: number,
  x2: number,
  y: number,
  obstacles: readonly EdgeObstacleBox[]
): boolean {
  if (obstacles.length === 0) return false;
  const left = Math.min(x1, x2);
  const right = Math.max(x1, x2);
  const pad = OBSTACLE_CLEARANCE_PX;
  for (const box of obstacles) {
    if (y < box.y - pad || y > box.y + box.height + pad) continue;
    if (right < box.x - pad || left > box.x + box.width + pad) continue;
    return true;
  }
  return false;
}

function bypassYAroundObstacles(
  x1: number,
  x2: number,
  y: number,
  obstacles: readonly EdgeObstacleBox[]
): number | null {
  const left = Math.min(x1, x2);
  const right = Math.max(x1, x2);
  const pad = OBSTACLE_CLEARANCE_PX;
  let above: number | null = null;
  let below: number | null = null;
  let blockerTop = Infinity;
  for (const box of obstacles) {
    if (y < box.y - pad || y > box.y + box.height + pad) continue;
    if (right < box.x - pad || left > box.x + box.width + pad) continue;
    blockerTop = Math.min(blockerTop, box.y);
    const top = box.y - pad - 8;
    const bottom = box.y + box.height + pad + 8;
    above = above == null ? top : Math.min(above, top);
    below = below == null ? bottom : Math.max(below, bottom);
  }
  if (above == null || below == null || !Number.isFinite(blockerTop)) return null;

  // Prefer merging onto an existing spine above the blocker (e.g. parent policy
  // center) so detours share one corridor instead of parallel ribbons.
  let bestCorridor: number | null = null;
  let bestDist = Infinity;
  for (const box of obstacles) {
    if (box.y + box.height > blockerTop + 0.5) continue;
    const spineY = box.y + box.height / 2;
    if (spineY > y + 1) continue;
    if (horizontalHitsObstacles(x1, x2, spineY, obstacles)) continue;
    const dist = Math.abs(y - spineY);
    if (dist < bestDist) {
      bestCorridor = spineY;
      bestDist = dist;
    }
  }
  if (bestCorridor != null) return bestCorridor;

  const distAbove = Math.abs(y - above);
  const distBelow = Math.abs(y - below);
  if (distAbove <= distBelow + 24) return above;
  return below;
}

function spansIntermediateColumn(sourceX: number, targetX: number): boolean {
  return (
    Math.abs(targetX - sourceX) >
    WORKSPACE_NODE_CARD_WIDTH + WORKSPACE_GRAPH_COLUMN_GAP * 0.75
  );
}

/**
 * Shared destination-gutter trunk X for long LR edges. Depends only on the
 * target handle so every edge into the same column stacks onto one vertical.
 */
function sharedApproachBendX(targetX: number, sourceX: number): number {
  const deltaX = targetX - sourceX;
  if (deltaX >= 0) {
    return targetX - Math.max(12, Math.min(WORKSPACE_EDGE_STUB_BASE, Math.max(12, deltaX - 24)));
  }
  return targetX + Math.max(12, Math.min(WORKSPACE_EDGE_STUB_BASE, Math.max(12, Math.abs(deltaX) - 24)));
}

/**
 * Prefer one shared trunk:
 * - short adjacent columns: source-gutter bend (sibling fan-out)
 * - long spans / blocked destination-Y runs: destination-gutter bend (parent spine)
 * Never mix early+late for the same long hop — that creates parallel ribbons.
 */
function resolveOrthogonalBendX(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  obstacles: readonly EdgeObstacleBox[]
): number {
  const early = pipelineBendX(sourceX, targetX);
  const late = sharedApproachBendX(targetX, sourceX);
  const earlyBlocked = horizontalHitsObstacles(early, targetX, targetY, obstacles);
  const lateBlocked = horizontalHitsObstacles(sourceX, late, sourceY, obstacles);
  const longHop = spansIntermediateColumn(sourceX, targetX);

  if (longHop || earlyBlocked) {
    if (!lateBlocked) return late;
    // Late H blocked too — still prefer destination gutter; caller may Y-detour.
    return late;
  }
  return early;
}

function buildObstacleDetourPath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  obstacles: readonly EdgeObstacleBox[]
): [path: string, labelX: number, labelY: number] | null {
  const gutterX = pipelineBendX(sourceX, targetX);
  const approachX = sharedApproachBendX(targetX, sourceX);
  const runY = bypassYAroundObstacles(gutterX, approachX, sourceY, obstacles);
  if (runY == null) return null;
  const path = roundOrthogonalPath([
    { x: sourceX, y: sourceY },
    { x: gutterX, y: sourceY },
    { x: gutterX, y: runY },
    { x: approachX, y: runY },
    { x: approachX, y: targetY },
    { x: targetX, y: targetY },
  ]);
  return [path, (gutterX + approachX) / 2, runY];
}

/** Corner radius for orthogonal elbows (road junctions). */
const EDGE_CORNER_RADIUS = 12;

type OrthoPoint = { x: number; y: number };

function almostEqual(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.5;
}

/**
 * Round every H/V corner independently. Short stubs get a smaller radius
 * instead of collapsing the whole path to sharp elbows.
 */
function roundOrthogonalPath(
  points: readonly OrthoPoint[],
  radius: number = EDGE_CORNER_RADIUS
): string {
  if (points.length === 0) return "";
  if (points.length === 1) {
    return `M ${points[0]!.x},${points[0]!.y}`;
  }
  if (points.length === 2) {
    const a = points[0]!;
    const b = points[1]!;
    if (almostEqual(a.y, b.y)) return `M ${a.x},${a.y} H ${b.x}`;
    if (almostEqual(a.x, b.x)) return `M ${a.x},${a.y} V ${b.y}`;
    return `M ${a.x},${a.y} L ${b.x},${b.y}`;
  }

  const start = points[0]!;
  const parts: string[] = [`M ${start.x},${start.y}`];

  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = points[i - 1]!;
    const curr = points[i]!;
    const next = points[i + 1]!;
    const inLen = Math.hypot(curr.x - prev.x, curr.y - prev.y);
    const outLen = Math.hypot(next.x - curr.x, next.y - curr.y);
    const r = Math.min(radius, Math.max(0, inLen / 2 - 0.5), Math.max(0, outLen / 2 - 0.5));

    if (r < 1.5) {
      if (almostEqual(prev.y, curr.y)) parts.push(`H ${curr.x}`);
      else if (almostEqual(prev.x, curr.x)) parts.push(`V ${curr.y}`);
      else parts.push(`L ${curr.x},${curr.y}`);
      continue;
    }

    const inNx = (curr.x - prev.x) / inLen;
    const inNy = (curr.y - prev.y) / inLen;
    const outNx = (next.x - curr.x) / outLen;
    const outNy = (next.y - curr.y) / outLen;
    const qStartX = curr.x - inNx * r;
    const qStartY = curr.y - inNy * r;
    const qEndX = curr.x + outNx * r;
    const qEndY = curr.y + outNy * r;

    if (almostEqual(prev.y, curr.y)) parts.push(`H ${qStartX}`);
    else if (almostEqual(prev.x, curr.x)) parts.push(`V ${qStartY}`);
    else parts.push(`L ${qStartX},${qStartY}`);
    parts.push(`Q ${curr.x},${curr.y} ${qEndX},${qEndY}`);
  }

  const last = points[points.length - 1]!;
  const prev = points[points.length - 2]!;
  if (almostEqual(prev.y, last.y)) parts.push(`H ${last.x}`);
  else if (almostEqual(prev.x, last.x)) parts.push(`V ${last.y}`);
  else parts.push(`L ${last.x},${last.y}`);

  return parts.join(" ");
}

function orthogonalElbowPath(
  sourceX: number,
  sourceY: number,
  bendX: number,
  targetX: number,
  targetY: number,
  radius: number = EDGE_CORNER_RADIUS
): string {
  return roundOrthogonalPath(
    [
      { x: sourceX, y: sourceY },
      { x: bendX, y: sourceY },
      { x: bendX, y: targetY },
      { x: targetX, y: targetY },
    ],
    radius
  );
}

/**
 * Orthogonal elbow only — never a diagonal L.
 * Sibling fan-outs share one trunk X (road + junctions): parent → trunk →
 * branch at each child Y. Rounded corners at the shared bend + each junction.
 */
function resolvePipelineEdgePath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  data?: WorkspacePipelineEdgeData | null,
  obstacles: readonly EdgeObstacleBox[] = []
): [path: string, labelX: number, labelY: number] {
  const deltaX = targetX - sourceX;
  const deltaY = targetY - sourceY;
  const absDeltaY = Math.abs(deltaY);
  const absDeltaX = Math.abs(deltaX);

  // Simulation reruns / derived stacks: stay orthogonal even when handles drift.
  if (data?.verticalStack || data?.centerAim) {
    if (absDeltaX <= PIPELINE_ROW_ALIGNMENT_PX && absDeltaY <= PIPELINE_ROW_ALIGNMENT_PX) {
      const path = `M ${sourceX},${sourceY} L ${targetX},${targetY}`;
      return [path, (sourceX + targetX) / 2, (sourceY + targetY) / 2];
    }
    if (absDeltaX <= PIPELINE_ROW_ALIGNMENT_PX) {
      const path = `M ${sourceX},${sourceY} V ${targetY}`;
      return [path, sourceX, (sourceY + targetY) / 2];
    }
    if (absDeltaY <= PIPELINE_ROW_ALIGNMENT_PX) {
      const path = `M ${sourceX},${sourceY} H ${targetX}`;
      return [path, (sourceX + targetX) / 2, sourceY];
    }
    const bendX = pipelineBendX(sourceX, targetX);
    const path = orthogonalElbowPath(sourceX, sourceY, bendX, targetX, targetY);
    return [path, bendX, (sourceY + targetY) / 2];
  }

  const detourY = data?.execDetourY;
  if (
    typeof detourY === "number" &&
    Number.isFinite(detourY) &&
    deltaX > WORKSPACE_EDGE_STUB_BASE * 2
  ) {
    // Underneath (or at) the combined stack: drop in the policy gutter, run
    // horizontal clear of the cards, then rise into the exec diamond.
    // Never share the policy→combined sibling trunk.
    const maxStub = Math.max(12, deltaX - 24);
    const stub = Math.min(maxStub, WORKSPACE_EDGE_STUB_BASE);
    const gutterX = sourceX + Math.max(12, stub);
    const approachX = Math.max(gutterX + 12, targetX - Math.max(12, stub));
    const runY = Math.max(detourY, targetY);
    if (Math.abs(runY - sourceY) <= PIPELINE_ROW_ALIGNMENT_PX) {
      const path = orthogonalElbowPath(sourceX, sourceY, gutterX, targetX, targetY);
      return [path, (gutterX + targetX) / 2, (sourceY + targetY) / 2];
    }
    const path = roundOrthogonalPath([
      { x: sourceX, y: sourceY },
      { x: gutterX, y: sourceY },
      { x: gutterX, y: runY },
      { x: approachX, y: runY },
      { x: approachX, y: targetY },
      { x: targetX, y: targetY },
    ]);
    return [path, (gutterX + approachX) / 2, runY];
  }

  // Same-column child with fixed side handles: exit further right into the
  // forward gutter, then enter the child's left.
  if (deltaX <= PIPELINE_ROW_ALIGNMENT_PX && absDeltaY > PIPELINE_ROW_ALIGNMENT_PX) {
    const bendX = sourceX + Math.max(12, WORKSPACE_EDGE_STUB_BASE);
    const path = orthogonalElbowPath(sourceX, sourceY, bendX, targetX, targetY);
    return [path, bendX, (sourceY + targetY) / 2];
  }

  if (deltaX > 0 && absDeltaY <= PIPELINE_ROW_ALIGNMENT_PX) {
    if (absDeltaY < 0.5 && !horizontalHitsObstacles(sourceX, targetX, sourceY, obstacles)) {
      const path = `M ${sourceX},${sourceY} H ${targetX}`;
      return [path, sourceX + deltaX / 2, sourceY];
    }
    if (horizontalHitsObstacles(sourceX, targetX, sourceY, obstacles)) {
      const detour = buildObstacleDetourPath(
        sourceX,
        sourceY,
        targetX,
        targetY,
        obstacles
      );
      if (detour) return detour;
    }
    // Tiny Y drift (snap/measure) — stay orthogonal instead of a visible diagonal.
    const bendX = resolveOrthogonalBendX(
      sourceX,
      sourceY,
      targetX,
      targetY,
      obstacles
    );
    const late = sharedApproachBendX(targetX, sourceX);
    if (
      bendX === late &&
      horizontalHitsObstacles(sourceX, late, sourceY, obstacles)
    ) {
      const detour = buildObstacleDetourPath(
        sourceX,
        sourceY,
        targetX,
        targetY,
        obstacles
      );
      if (detour) return detour;
    }
    const path = orthogonalElbowPath(sourceX, sourceY, bendX, targetX, targetY);
    return [path, bendX, (sourceY + targetY) / 2];
  }

  const bendX = resolveOrthogonalBendX(
    sourceX,
    sourceY,
    targetX,
    targetY,
    obstacles
  );
  const late = sharedApproachBendX(targetX, sourceX);
  if (
    bendX === late &&
    horizontalHitsObstacles(sourceX, late, sourceY, obstacles)
  ) {
    const detour = buildObstacleDetourPath(
      sourceX,
      sourceY,
      targetX,
      targetY,
      obstacles
    );
    if (detour) return detour;
  }
  const path = orthogonalElbowPath(sourceX, sourceY, bendX, targetX, targetY);
  return [path, bendX, (sourceY + targetY) / 2];
}

/**
 * Placeholder branch only — when real siblings share the trunk, start at the
 * split (lowest sibling Y on the shared bend) so the dark pipeline edges keep
 * the parent→trunk color.
 */
function resolvePlaceholderBranchPath(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  trunkJoinY: number | null | undefined,
  _data?: WorkspacePlaceholderEdgeData | null
): string {
  const absDeltaY = Math.abs(targetY - sourceY);

  // Empty next-step (no siblings): stay on the parent spine — pure horizontal.
  if (
    (trunkJoinY == null || !Number.isFinite(trunkJoinY)) &&
    absDeltaY <= PIPELINE_ROW_ALIGNMENT_PX
  ) {
    if (absDeltaY < 0.5) {
      return `M ${sourceX},${sourceY} H ${targetX}`;
    }
    const bendX = pipelineBendX(sourceX, targetX);
    return orthogonalElbowPath(sourceX, sourceY, bendX, targetX, targetY);
  }

  if (
    trunkJoinY == null ||
    !Number.isFinite(trunkJoinY) ||
    targetY <= trunkJoinY + PIPELINE_ROW_ALIGNMENT_PX
  ) {
    return resolvePipelineEdgePath(sourceX, sourceY, targetX, targetY)[0];
  }

  const bendX = pipelineBendX(sourceX, targetX);
  // Continue the shared trunk, then junction into the placeholder diamond.
  if (Math.abs(targetY - trunkJoinY) < 0.5) {
    return `M ${bendX},${trunkJoinY} H ${targetX}`;
  }
  return roundOrthogonalPath([
    { x: bendX, y: trunkJoinY },
    { x: bendX, y: targetY },
    { x: targetX, y: targetY },
  ]);
}

function readFlowNodeCenterY(node: {
  position: { x: number; y: number };
  height?: number | null;
  measured?: { height?: number };
}): number {
  const height =
    typeof node.height === "number" && node.height > 0
      ? node.height
      : typeof node.measured?.height === "number" && node.measured.height > 0
        ? node.measured.height
        : WORKSPACE_NODE_CARD_HEIGHT;
  return node.position.y + height / 2;
}

export function WorkspacePipelineEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  selected,
  animated,
  data,
}: EdgeProps): React.ReactElement {
  const edgeData = (data ?? undefined) as WorkspacePipelineEdgeData | undefined;
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const floating =
    edgeData?.centerAim && sourceNode && targetNode
      ? getWorkspaceFloatingEdgeParams(sourceNode, targetNode)
      : null;

  const obstacles = useStore(
    React.useCallback(
      (state: {
        nodes: Array<{
          id: string;
          hidden?: boolean;
          position: { x: number; y: number };
          width?: number | null;
          height?: number | null;
          measured?: { width?: number; height?: number };
          internals?: { positionAbsolute?: { x: number; y: number } };
        }>;
        nodeLookup?: Map<string, {
          id: string;
          hidden?: boolean;
          position: { x: number; y: number };
          width?: number | null;
          height?: number | null;
          measured?: { width?: number; height?: number };
          internals?: { positionAbsolute?: { x: number; y: number } };
        }>;
      }) => {
        const list = state.nodeLookup
          ? Array.from(state.nodeLookup.values())
          : state.nodes;
        const boxes: EdgeObstacleBox[] = [];
        for (const node of list) {
          if (!node || node.hidden) continue;
          if (node.id === source || node.id === target) continue;
          const nodeType = (node as { type?: string }).type;
          if (nodeType && nodeType !== "workspace") continue;
          const abs =
            node.internals?.positionAbsolute ?? node.position;
          const width =
            (typeof node.measured?.width === "number" && node.measured.width > 0
              ? node.measured.width
              : typeof node.width === "number" && node.width > 0
                ? node.width
                : WORKSPACE_NODE_CARD_WIDTH) || WORKSPACE_NODE_CARD_WIDTH;
          const height =
            (typeof node.measured?.height === "number" && node.measured.height > 0
              ? node.measured.height
              : typeof node.height === "number" && node.height > 0
                ? node.height
                : WORKSPACE_NODE_CARD_HEIGHT) || WORKSPACE_NODE_CARD_HEIGHT;
          boxes.push({ x: abs.x, y: abs.y, width, height });
        }
        return boxes;
      },
      [source, target]
    ),
    (a: EdgeObstacleBox[], b: EdgeObstacleBox[]) => {
      if (a.length !== b.length) return false;
      return a.every(
        (box, i) =>
          box.x === b[i]?.x &&
          box.y === b[i]?.y &&
          box.width === b[i]?.width &&
          box.height === b[i]?.height
      );
    }
  );

  const [edgePath] = resolvePipelineEdgePath(
    floating?.sx ?? sourceX,
    floating?.sy ?? sourceY,
    floating?.tx ?? targetX,
    floating?.ty ?? targetY,
    edgeData,
    obstacles
  );

  const onLoadingPath = Boolean(edgeData?.loadingPath || animated);
  const focusFilterDimmed = Boolean(edgeData?.focusFilterDimmed);
  const combinedSourceHighlight = Boolean(edgeData?.combinedSourceHighlight);

  return (
    <BaseEdge
      id={id}
      path={edgePath}
      interactionWidth={20}
      className={[
        "workspace-edge-pipeline",
        onLoadingPath ? "workspace-edge-loading-path" : null,
        selected ? "workspace-edge-selected" : null,
        combinedSourceHighlight ? "workspace-edge-combined-source" : null,
        focusFilterDimmed ? "workspace-edge-focus-dimmed" : null,
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        strokeWidth:
          selected || combinedSourceHighlight ? 2.25 : onLoadingPath ? 2.35 : 2,
        stroke: selected
          ? "var(--primary)"
          : onLoadingPath
            ? LOADING_PATH_STROKE
            : combinedSourceHighlight
              ? COMBINED_SOURCE_STROKE
              : EDGE_STROKE,
        fill: "none",
        strokeLinecap: "round",
        strokeLinejoin: "round",
        opacity:
          focusFilterDimmed && !selected && !onLoadingPath && !combinedSourceHighlight
            ? 0.28
            : undefined,
      }}
    />
  );
}

export function WorkspacePlaceholderEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  data,
}: EdgeProps): React.ReactElement {
  const edgeData = (data ?? undefined) as WorkspacePlaceholderEdgeData | undefined;
  const siblingKey = (edgeData?.siblingNodeIds ?? []).join("|");

  const trunkJoinY = useStore(
    React.useCallback(
      (state: {
        nodes: Array<{
          id: string;
          position: { x: number; y: number };
          height?: number | null;
          measured?: { height?: number };
        }>;
        nodeLookup?: Map<
          string,
          {
            id: string;
            position: { x: number; y: number };
            height?: number | null;
            measured?: { height?: number };
          }
        >;
      }) => {
        if (!siblingKey) return null;
        const siblingNodeIds = siblingKey.split("|").filter(Boolean);
        let lowest = -Infinity;
        for (const siblingId of siblingNodeIds) {
          const node =
            state.nodeLookup?.get(siblingId) ??
            state.nodes.find((candidate) => candidate.id === siblingId);
          if (!node) continue;
          lowest = Math.max(lowest, readFlowNodeCenterY(node));
        }
        return Number.isFinite(lowest) && lowest !== -Infinity ? lowest : null;
      },
      [siblingKey]
    )
  );

  const edgePath = resolvePlaceholderBranchPath(
    sourceX,
    sourceY,
    targetX,
    targetY,
    trunkJoinY,
    edgeData
  );

  return (
    <BaseEdge
      id={id}
      path={edgePath}
      interactionWidth={12}
      className="workspace-edge-placeholder"
      style={{
        strokeWidth: 1.75,
        stroke: PLACEHOLDER_EDGE_STROKE,
        fill: "none",
        strokeLinecap: "round",
        strokeLinejoin: "round",
      }}
    />
  );
}

export const workspaceEdgeTypes = {
  workspacePipeline: WorkspacePipelineEdge,
  workspacePlaceholder: WorkspacePlaceholderEdge,
};

/** @internal Exported for unit tests. */
export function workspacePipelineEdgePathForTest(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  data?: WorkspacePipelineEdgeData,
  obstacles: readonly EdgeObstacleBox[] = []
): string {
  return resolvePipelineEdgePath(sourceX, sourceY, targetX, targetY, data, obstacles)[0];
}

/** @internal Exported for unit tests. */
export function workspacePlaceholderBranchPathForTest(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  trunkJoinY?: number | null,
  data?: WorkspacePlaceholderEdgeData
): string {
  return resolvePlaceholderBranchPath(sourceX, sourceY, targetX, targetY, trunkJoinY, data);
}
